import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getDateNumber } from '../../app/dates';
import { RootState } from '../../app/store';
import { AuthorizedRequestArg } from '../signin/auth';
import * as authFetch from '../signin/authFetch';
import { Entry, EntryUpdate, Measurement } from './types';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

interface GetEntryArg extends AuthorizedRequestArg {
  entry: EntryAddress
};

interface GetEntryRequest {
  getEntry: EntryAddress;
};

interface GetEntryResponse {
  entry: Entry;
};

interface EntryAddress {
  measurementID: string;
  entryID: string;
};

interface EntryState {
  status: Status;
  error: string | null;
  entry: Entry | null;
};

interface MeasurementsState {
  status: Status;
  error: string | null;
  list: Array<Measurement>;
  entries: { [key: string]: EntryState };
  entryDate: number;
};

interface ListMeasurementsRequest {
  listMeasurements: object;
}

interface ListMeasurementsResponse {
  measurements: Array<Measurement>
}

interface WriteEntryArg extends AuthorizedRequestArg {
  measurementID: string;
  entry: EntryUpdate;
};

interface WriteEntryRequest {
  writeEntry: WriteEntryArg;
};

interface WriteEntryResponse {
  entry: Entry;
};

const getEntryStateKey = (addr: EntryAddress) =>
  `${addr.measurementID}|${addr.entryID}`;

const getEntryState = (state: MeasurementsState, addr: EntryAddress): EntryState | null => {
  return state.entries[getEntryStateKey(addr)] || null;
};

const ensureEntryState = (state: MeasurementsState, addr: EntryAddress): EntryState => {
  const key = getEntryStateKey(addr);
  let entry = state.entries[key];
  if (!entry) {
    entry = {
      status: 'idle',
      error: null,
      entry: null,
    };
    state.entries[key] = entry;
  }
  return entry;
}

export const getEntry = createAsyncThunk('measurements/getEntry', async (arg: GetEntryArg, { dispatch }) => {
  const response = await authFetch.post<GetEntryRequest, GetEntryResponse>(
    `${process.env.REACT_APP_SERVER_ROOT}/Goals`,
    {
      'getEntry': arg.entry,
    },
    arg.authToken,
    dispatch,
  );
  return response.data?.entry || null;
});

export const writeEntry = createAsyncThunk('measurements/writeEntry', async (arg: WriteEntryArg, { dispatch }) => {
  const response = await authFetch.post<WriteEntryRequest, WriteEntryResponse>(
    `${process.env.REACT_APP_SERVER_ROOT}/Goals`,
    {
      'writeEntry': arg,
    },
    arg.authToken,
    dispatch,
  );
  return response.data?.entry || null;
});


export const listMeasurements = createAsyncThunk('measurements/listMeasurements', async (arg: AuthorizedRequestArg, { dispatch }) => {
  try {
    const response = await authFetch.post<ListMeasurementsRequest, ListMeasurementsResponse>(
      `${process.env.REACT_APP_SERVER_ROOT}/Goals`,
      {
        'listMeasurements': {},
      },
      arg.authToken,
      dispatch
    )
    return response.data?.measurements || [];
  } catch (err) {
    console.log('Error', err);
  }
});

export const measurementsSlice = createSlice({
  name: 'measurements',
  initialState: {
    list: [],
    status: 'idle',
    error: null,
    entryDate: getDateNumber(),
    entries: {},
  } as MeasurementsState,
  reducers: {
    setEntryDate: (state, action) => {
      state.entryDate = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(listMeasurements.pending, (state, action) => {
      state.status = 'loading';
    });
    builder.addCase(listMeasurements.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.list = action.payload || [];
    });
    builder.addCase(listMeasurements.rejected, (state, action) => {
      state.status = 'failed';
      console.log('Error', action.error.message)
      state.error = action.error.message || null;
    });

    builder.addCase(getEntry.pending, (state, action) => {
      const entryState = ensureEntryState(state, action.meta.arg.entry);
      entryState.status = 'loading';
    });
    builder.addCase(getEntry.fulfilled, (state, action) => {
      const entryState = ensureEntryState(state, action.meta.arg.entry);
      entryState.status = 'succeeded';
      console.log('Success', action.payload);
      entryState.entry = action.payload;
    });
    builder.addCase(getEntry.rejected, (state, action) => {
      const entryState = ensureEntryState(state, action.meta.arg.entry);
      entryState.status = 'failed';
      console.log('Error', action.error.message)
      entryState.error = action.error.message || null;
    });

    builder.addCase(writeEntry.pending, (state, action) => {
      const addr = { measurementID: action.meta.arg.measurementID, entryID: action.meta.arg.entry.id };
      const entryState = ensureEntryState(state, addr);
      entryState.status = 'loading';
    });
    builder.addCase(writeEntry.fulfilled, (state, action) => {
      const addr = { measurementID: action.meta.arg.measurementID, entryID: action.meta.arg.entry.id };
      const entryState = ensureEntryState(state, addr);
      entryState.status = 'succeeded';
      console.log('Success', action.payload);
      entryState.entry = action.payload;
    });
    builder.addCase(writeEntry.rejected, (state, action) => {
      const addr = { measurementID: action.meta.arg.measurementID, entryID: action.meta.arg.entry.id };
      const entryState = ensureEntryState(state, addr);
      entryState.status = 'failed';
      console.log('Error', action.error.message)
      entryState.error = action.error.message || null;
    });
  }
});

export const { setEntryDate } = measurementsSlice.actions

export const selectEntryDate = (state: RootState) =>
  state.measurements.entryDate;

export const selectAllMeasurements = (state: RootState) =>
  state.measurements.list;

export const selectMeasurementByID = (state: RootState, id: string) =>
  state.measurements.list.find(m => m.id === id) || null;

export const selectEntryStateByID = (state: RootState, addr: EntryAddress) =>
  getEntryState(state.measurements, addr);

export default measurementsSlice.reducer;